import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { routes } from "../../../../constants/routes";
import { useLocation, useNavigate } from "react-router-dom";
import CommunicationSearchAndFilters from "./search-and-filters";
import CommunicationFilterPanel, {
  FilterOptions,
} from "./communication-filter-panel";
import "./communication-management.scss";
import ListingTable from "../../../../common/components/listing-table/list-table";
import {
  getSupportTickets,
  updateTicketStatus,
  SupportTicket,
} from "../../../../api/admin/communication-management";
import {
  actionOptions,
  closedHeaders,
  headers,
  STATUS_OPTIONS,
} from "./communication-management-helper";
import dayjs from "dayjs";
import AdminPaginationComponent from "../../../../common/components/admin-pagination-component/admin-pagination-component";
import { useSetLoading } from "../../../../common/hooks/appearance";
import {
  AlertComponent,
  SelectInputComponent,
} from "../../../../common/components";
import ProfileHeader from "../../../../common/components/profile-header/profile-header-and-breadcrumbs";
import { Box, Tabs, Tab, FormControl } from "@mui/material";
import _ from "lodash";
import MarkAsClosedPopup from "./mark-as-closed-popup";
import ConfirmationPopup from "../../../../common/components/confirmation-popup/confirmation-popup";
import InfoIcon from "../../../../assets/icons/info-icon.svg";
import ActionConfirmationPopup from "../../../../common/components/action-confirmation-popup/action-confirmation-popup";
import {
  getProviderRequests,
  getProviderProfiles,
} from "../../../../api/admin/provider-management";
import { BUSINESS_TYPE_PLANNER } from "../../../../utils/constants";
import { useDispatch, useSelector } from "react-redux";
const CommunicationManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<number>(0);
  const breadcrumbLinkForList = [
    {
      label: "Support Requests",
      link: routes.ADMIN_COMMUNICATION_MANAGEMENT,
      handleClick: () => {},
      disablePointerEvents: true,
    },
  ];
  const [breadcrumbLink, setBreadcrumbLink] = useState(breadcrumbLinkForList);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<SupportTicket[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [error, setError] = useState("");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [searchText, setSearchText] = useState<any>(
    location?.state?.searchText || "",
  );
  const [isResetSearch, setIsResetSearch] = useState(false);
  const [isMarkAsClosedOpen, setIsMarkAsClosedOpen] = useState(false);
  const searchRef = useRef<{ clear: () => void }>(null);
  const isSearchTriggered = useRef(false);
  const pageSize = 10;
  const [statusUpdateLoading, setStatusUpdateLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const [filterPanelOpen, setFilterPanelOpen] = useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<FilterOptions>({
    partners: [],
    planners: [],
    status: [],
    dateRaisedFrom: null,
    dateRaisedTo: null,
    dateClosedFrom: null,
    dateClosedTo: null,
  });
  const [selectedTicketStatus, setSelectedTicketStatus] = useState<{
    label: string;
    val: string;
  }>({
    label: "Open",
    val: "Open",
  });
  const [currentTicketId, setCurrentTicketId] = useState<number | null>(null);
  const [isReOpenOpen, setIsReOpenOpen] = useState<boolean>(false);
  const [availablePartners, setAvailablePartners] = useState<any[]>([]);
  const adminSupportRefresh = useSelector(
    (state: any) => state?.support.adminSupportRefresh,
  );
  const dispatch = useDispatch();

  const handleSearch = useCallback(
    _.debounce((text: string) => {
      isSearchTriggered.current = true;
      setCurrentPage(1);
      setSearchText(text);
      if (text === "") {
        setIsResetSearch(true);
        setTimeout(() => setIsResetSearch(false), 100);
      }
    }, 500),
    [],
  );

  const resetSearch = () => {
    setSearchText("");
    if (searchRef.current) {
      searchRef.current.clear();
    }
  };
  const fetchSupportTickets = async (
    page: number,
    search: string,
    tabIndex: number,
    filters?: FilterOptions,
  ) => {
    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      const params: any = {
        limit: pageSize,
        offset,
      };

      if (search) {
        params.searchText = search;
      }

      const filtersSelected =
        filters &&
        ((filters.partners && filters.partners.length > 0) ||
          (filters.planners && filters.planners.length > 0) ||
          (filters.status && filters.status.length > 0) ||
          filters.dateRaisedFrom ||
          filters.dateRaisedTo ||
          filters.dateClosedFrom ||
          filters.dateClosedTo);

      if (!filtersSelected) {
        params.closedTab = tabIndex === 1 ? "true" : "false";
      }

      if (filters) {
        if (
          (filters.partners && filters.partners.length > 0) ||
          (filters.planners && filters.planners.length > 0)
        ) {
          const combinedPartners = [
            ...(filters.partners || []),
            ...(filters.planners || []),
          ];
          params.partners = combinedPartners;
        }

        if (filters.status && filters.status.length > 0) {
          const apiStatus = filters.status.map((s) => {
            if (s === "In Progress") return "InProgress";
            if (s === "Reopen") return "ReOpen";
            return s;
          });
          params.status = apiStatus;
        }

        if (filters.dateRaisedFrom && filters.dateRaisedTo) {
          params.dateRaisedStart = filters.dateRaisedFrom
            .startOf("day")
            .toISOString();
          params.dateRaisedEnd = filters.dateRaisedTo
            .endOf("day")
            .toISOString();
        }

        if (filters.dateClosedFrom && filters.dateClosedTo) {
          params.dateClosedStart = filters.dateClosedFrom
            .startOf("day")
            .toISOString();
          params.dateClosedEnd = filters.dateClosedTo
            .endOf("day")
            .toISOString();
        }
      }

      const response = await getSupportTickets(params);
      if (response?.status === 200 || response?.data?.success) {
        const tickets = response?.data?.data?.data?.supportRequests || [];
        const totalRecords = response?.data?.data?.data?.totalRecords || 0;

        const mappedTickets = tickets.map((ticket: any) => ({
          ...ticket,
          ticketId: ticket.ticket_id || ticket.ticketId,
          providerName: ticket.partner?.businessName || ticket.name,
          contactEmail:
            ticket.contact_email ||
            ticket.contactEmail ||
            ticket.partner?.email,
          contactNumber: ticket.contact_number || ticket.contactNumber,
          createdAt: ticket.created_at || ticket.createdAt,
        }));

        setData(mappedTickets);
        setTotalRecords(totalRecords);
        dispatch({
          type: "SUPPORT:SET_ADMIN_SUPPORT_INDICATOR",
          payload: { value: false },
        });
      } else {
        setAlertType("error");
        setAlertMessage("Failed to fetch support tickets.");
        setAlertOpen(true);
      }
    } catch (error: any) {
      setAlertType("error");
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch support tickets.",
      );
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const refreshPage = async () => {
      if (adminSupportRefresh) {
        setCurrentPage(1);
        setActiveTab(0);
        await fetchSupportTickets(1, searchText || "", 0, activeFilters);
        isSearchTriggered.current = false;
        dispatch({
          type: "SUPPORT:REFRESH_ADMIN_SUPPORT_PAGE",
          payload: { value: false },
        });
      }
    };
    refreshPage();
  }, [adminSupportRefresh]);

  const fetchData = async () => {
    const pageToUse = isSearchTriggered.current ? 1 : currentPage;
    await fetchSupportTickets(
      pageToUse,
      searchText || "",
      activeTab,
      activeFilters,
    );
    isSearchTriggered.current = false;
    const [requestsRes, plannersRes] = await Promise.all([
      getProviderRequests({ forDropdown: true, status: "approved" }),
      getProviderProfiles({
        businessTypes: [BUSINESS_TYPE_PLANNER],
        limit: 500,
        page: 1,
      }),
    ]);
    const byId: Record<
      number,
      { id: number; businessName: string; type: string }
    > = {};
    (requestsRes?.data?.data?.data || []).forEach((item: any) => {
      byId[item.id] = {
        id: item.id,
        businessName: item.businessName,
        type: item.businessType || item.businessInformation?.businessType || "",
      };
    });
    (plannersRes?.data?.data?.data || []).forEach((item: any) => {
      byId[item.id] = {
        id: item.id,
        businessName: item.businessName,
        type: BUSINESS_TYPE_PLANNER,
      };
    });
    setAvailablePartners(Object.values(byId));
  };
  useEffect(() => {
    fetchData();
  }, [currentPage, searchText, activeTab, activeFilters]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setCurrentPage(value);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveFilters({
      partners: [],
      planners: [],
      status: [],
      dateRaisedFrom: null,
      dateRaisedTo: null,
      dateClosedFrom: null,
      dateClosedTo: null,
    });
    setActiveTab(newValue);
    setCurrentPage(1);
  };

  const handleStatusChange = async (
    ticketId: number,
    newStatus: "Open" | "In Progress",
  ) => {
    setStatusUpdateLoading((prev) => ({ ...prev, [ticketId]: true }));
    try {
      const apiStatus = newStatus === "In Progress" ? "InProgress" : "Open";
      const response = await updateTicketStatus(ticketId, {
        status: apiStatus,
      });

      if (response?.status === 200 || response?.data?.success) {
        setAlertMessage("Ticket status updated successfully.");
        setAlertType("success");
        setAlertOpen(true);

        await fetchSupportTickets(
          currentPage,
          searchText || "",
          activeTab,
          activeFilters,
        );
      } else {
        setAlertMessage("Failed to update ticket status.");
        setAlertType("error");
        setAlertOpen(true);
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to update ticket status.",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setStatusUpdateLoading((prev) => ({ ...prev, [ticketId]: false }));
    }
  };
  const formatStatus = (status: string) => {
    switch (status) {
      case "InProgress":
        return "In Progress";
      case "ReOpen":
        return "Reopen";
      default:
        return status;
    }
  };
  const formattedData = useMemo(() => {
    if (Array.isArray(data)) {
      return data.map((item: SupportTicket) => {
        const ticketId = item.ticket_id;

        const providerName =
          item.partner?.businessName || item.providerName || item.name || "-";
        const dateRaised = item.created_at || "-";
        const dateClosed = item.closed_date || "-";
        const formattedDate =
          dateRaised !== "-" ? dayjs(dateRaised).format("MM/DD/YYYY") : "-";
        const formattedClosedDate =
          dateClosed !== "-" ? dayjs(dateClosed).format("MM/DD/YYYY") : "-";
        return {
          id: item.id,
          ticketId: ticketId,
          numericId: item.id,
          providerName: providerName,
          subject: item.subject || "-",
          status:
            item.status === "Closed" ? (
              item.status
            ) : (
              <>
                <FormControl
                  size="small"
                  sx={{ minWidth: "auto", width: "auto" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <SelectInputComponent
                    fieldName="status"
                    options={STATUS_OPTIONS}
                    value={
                      selectedTicketStatus.label === item.status
                        ? formatStatus(selectedTicketStatus.val)
                        : formatStatus(item.status)
                    }
                    onChange={(value: any) => {
                      setSelectedTicketStatus({
                        label: value,
                        val: value,
                      });
                      handleStatusChange(
                        item.id,
                        value as "Open" | "In Progress",
                      );
                    }}
                    placeholder=""
                    isFilter={false}
                  />
                </FormControl>
              </>
            ),
          dateRaised: formattedDate,
          dateClosed: formattedClosedDate,
          action: "verticalEllipse",
          actionOptions: actionOptions(item.status),
        };
      });
    }
    return [];
  }, [data, statusUpdateLoading, searchText, activeTab, navigate]);

  useEffect(() => {
    if (
      location.state &&
      location.state.alertMessage &&
      location?.state?.fromView
    ) {
      setAlertMessage(location.state.alertMessage);
      setAlertType(location.state.alertType || "info");
      setAlertOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (location.state && location.state.searchText) {
      const searchValue = location.state.searchText;
      setSearchText(searchValue);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (location.state && location.state.activeTab !== undefined) {
      setActiveTab(location.state.activeTab);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);
  const appliedFilterCount = useMemo(() => {
    let count = 0;
    // Partners
    if (
      (activeFilters.partners && activeFilters.partners.length > 0) ||
      (activeFilters.planners && activeFilters.planners.length > 0)
    )
      count++;
    // Status
    if (activeFilters.status && activeFilters.status.length > 0) count++;
    // Date Raised Range
    if (activeFilters.dateRaisedFrom && activeFilters.dateRaisedTo) count++;
    // Date Closed Range
    if (activeFilters.dateClosedFrom && activeFilters.dateClosedTo) count++;
    return count;
  }, [activeFilters]);
  const noResultsTxt = useMemo(() => {
    if (loading) {
      return "Loading tickets...";
    }
    const hasActiveFilters =
      activeFilters.partners.length > 0 ||
      activeFilters.planners.length > 0 ||
      activeFilters.status.length > 0 ||
      activeFilters.dateRaisedFrom !== null ||
      activeFilters.dateRaisedTo !== null;

    if (searchText || hasActiveFilters) {
      return "No tickets found matching your search criteria.";
    }
    return "No tickets found";
  }, [loading, searchText, activeFilters]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  const handleFilterApply = (filters: FilterOptions) => {
    setActiveFilters(filters);
    setCurrentPage(1);
  };

  const handleFilterReset = () => {
    setActiveFilters({
      partners: [],
      planners: [],
      status: [],
      dateRaisedFrom: null,
      dateRaisedTo: null,
      dateClosedFrom: null,
      dateClosedTo: null,
    });
    setCurrentPage(1);
  };

  const hasActiveFilters = useMemo(() => {
    return (
      (activeFilters.partners && activeFilters.partners.length > 0) ||
      (activeFilters.planners && activeFilters.planners.length > 0) ||
      (activeFilters.status && activeFilters.status.length > 0) ||
      activeFilters.dateRaisedFrom ||
      activeFilters.dateRaisedTo ||
      activeFilters.dateClosedFrom ||
      activeFilters.dateClosedTo
    );
  }, [activeFilters]);
  const getAlertMessageBasedOnStatus = (status: string) => {
    switch (status) {
      case "Closed":
        return "Ticket closed successfully.";
      case "ReOpen":
        return "Ticket reopened successfully.";
      default:
        return "Ticket status updated successfully.";
    }
  };
  const fetchTicketsIncludingSearchAndFilters = () => {
    if (currentPage === 1) {
      if (searchText || appliedFilterCount > 0) {
        fetchSupportTickets(1, searchText || "", activeTab, activeFilters);
      } else fetchSupportTickets(1, searchText || "", activeTab, activeFilters);
    } else {
      setCurrentPage(1);
    }
  };
  const updateTicketStatusAPI = async (
    ticketId: number,
    data: {
      status: "Open" | "InProgress" | "Closed" | "ReOpen";
      comments?: string;
    },
  ) => {
    try {
      setLoading(true);

      const result = await updateTicketStatus(ticketId, data);
      if (result?.status === 200) {
        setAlertMessage(getAlertMessageBasedOnStatus(data?.status));
        setAlertType("success");
        setAlertOpen(true);
        // Refresh the tickets list
        setCurrentPage(1);
        fetchTicketsIncludingSearchAndFilters();
      } else {
        setAlertMessage("Failed to update ticket status. Please try again.");
        setAlertType("error");
        setAlertOpen(true);
      }
    } catch (error: any) {
      console.error("Error updating ticket status:", error);
      setAlertMessage(
        error?.response?.data?.message ||
          "Failed to update ticket status. Please try again.",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };
  const handleMarkAsClosed = async (value: string) => {
    // Implement the logic to mark the ticket as closed
    if (currentTicketId) {
      await updateTicketStatusAPI(currentTicketId, {
        status: "Closed",
        comments: value,
      });
      setIsMarkAsClosedOpen(false);
    }
  };
  const handleReOpen = async () => {
    // Implement the logic to reopen the ticket
    if (currentTicketId) {
      await updateTicketStatusAPI(currentTicketId, { status: "ReOpen" });
      setIsReOpenOpen(false);
    }
  };
  const handleAction = (actionType: string, id: number) => {
    setCurrentTicketId(id);
    if (actionType === "View") {
      const numericId = id;
      navigate(
        `${routes.ADMIN_COMMUNICATION_TICKET_DETAILS.replace(
          ":id",
          numericId.toString(),
        )}`,
        {
          state: {
            searchText: searchText,
            activeTab: activeTab,
          },
        },
      );
    } else if (actionType === "Mark As Closed") {
      // Implement mark as closed functionality here
      setIsMarkAsClosedOpen(true);
    } else if (actionType === "Reopen") {
      //implement re open functionality here
      setIsReOpenOpen(true);
    }
  };
  return (
    <>
      <div className="communication-management-main-wrapper">
        <ProfileHeader breadcrumbLink={breadcrumbLink} />
        <Box className="communication-management-content">
          <div
            className="communication-management-filter-and-list-wrapper"
          >
            <Tabs
              value={hasActiveFilters ? false : activeTab}
              onChange={handleTabChange}
              className="communication-management-tabs"
              sx={{
                borderBottom: "1px solid #e5e7eb",
                "& .MuiTab-root": {
                  textTransform: "none !important",
                  color: "#6a7282 !important",
                },
                "& .MuiTab-root.Mui-selected": {
                  color: "#009689 !important",
                  backgroundColor: "transparent !important",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#009689 !important",
                },
              }}
            >
              <Tab label="Active Tickets" />
              <Tab label="Closed Tickets" />
            </Tabs>
            <div className="communication-management-search-and-filters">
              <CommunicationSearchAndFilters
                onSearch={handleSearch}
                searchRef={searchRef as React.RefObject<{ clear: () => void }>}
                localSearchText={searchText}
                isReset={isResetSearch}
                placeholder="Search by Ticket ID, Provider Name, Subject"
                onFilterClick={() => setFilterPanelOpen(true)}
                appliedFiltersCount={appliedFilterCount}
              />
            </div>
            <div className="communication-management-listing-table-wrapper">
              <ListingTable
                headers={activeTab === 0 ? headers : closedHeaders}
                data={formattedData}
                onRowClick={(row) => {
                  const numericId = row.numericId || row.id;

                  navigate(
                    `${routes.ADMIN_COMMUNICATION_TICKET_DETAILS.replace(
                      ":id",
                      numericId.toString(),
                    )}`,
                    {
                      state: {
                        searchText: searchText,
                        activeTab: activeTab,
                      },
                    },
                  );
                }}
                handleAction={(actionType: string, id: number) =>
                  handleAction(actionType, id)
                }
                actionWidth="130px"
                noResultsText={noResultsTxt}
              />
            </div>
            <MarkAsClosedPopup
              open={isMarkAsClosedOpen}
              onCancel={() => setIsMarkAsClosedOpen(false)}
              onOkay={handleMarkAsClosed}
              title="Mark As Closed"
              description="Are you sure you want to mark this ticket as closed?"
            />
            <ConfirmationPopup
              open={isReOpenOpen}
              onCancel={() => setIsReOpenOpen(false)}
              onOkay={handleReOpen}
              title="Reopen Ticket"
              Icon={InfoIcon}
              buttonText="Reopen"
              note="Are you sure you want to reopen this ticket?"
            />
            {totalRecords > 0 && (
              <AdminPaginationComponent
                totalRecords={totalRecords}
                pageSize={pageSize}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </Box>
      </div>
      <AlertComponent
        open={alertOpen}
        type={alertType}
        message={alertMessage || ""}
        onClose={() => setAlertOpen(false)}
      />
      <CommunicationFilterPanel
        open={filterPanelOpen}
        onClose={() => setFilterPanelOpen(false)}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
        initialFilters={activeFilters}
        availablePartners={availablePartners}
      />
    </>
  );
};

export default CommunicationManagement;
